import { toastError } from "@helpers/utils";
import { resetOperation, startOperation } from "@redux/features/dfs/dfsSlice";
import { AppThunk } from "@redux/hooks";
import { Integration } from "@/types/general";

// ================================================================================
// EXPORT
// ================================================================================

type ModulesWithExportSupport =
    | "product"
    | "product_draft"
    | "stock"
    | "purchase_order"
    | "sale_order"
    | "transfer"
    | "stock_count";

/**
 * Thunk to perform an export for a specific module. It incorporates the necessary logic to start the export process and everything else will be held by the sockets listeners. * You must create export logic ONLY here, otherwise talk to @nekear about this.
 * @param module
 * @param query
 */
export function performExport<T>(
    module: ModulesWithExportSupport,
    query: () => Promise<T>
): AppThunk {
    return async (dispatch, getState) => {
        try {
            // Optional. Sockets listeners will catch the ExportStarted event and start the export process, however it will require some time and such manual start improves UX.
            // @see src/components/DashboardNav/components/ExportIndicators.tsx
            dispatch(startOperation({ operation: "export", module }));

            return await query();
        } catch (e: any) {
            toastError(e);

            console.log(`Error while performing an export for module [${module}]}:`, e);
            dispatch(resetOperation({ operation: "export" }));
            return Promise.reject(e);
        }
    };
}

// ================================================================================
// IMPORT
// ================================================================================

type ModulesWithImportSupport =
    | "tripletex"
    | "product"
    | "product_draft"
    | "poweroffice"
    | "twenty_four_seven_office"
    | "shopify"
    | "susoft";

type PerformImportOptions = {
    shouldSuppressErrorToast?: (error: unknown) => boolean;
    /**
     * Entities covered by an integration import, so the UI can tell which records are being imported (e.g., customers-only sync).
     */
    entities?: Integration.Import.Entity[];
};

/**
 * Thunk to perform an import for a specific module. It incorporates the necessary logic to start the import process and everything else will be held by the sockets listeners.
 * You must create imports logic ONLY here, otherwise talk to @nekear about this.
 * @param module
 * @param query
 */
export const performImport =
    <T>(
        module: ModulesWithImportSupport,
        query: () => Promise<T>,
        options?: PerformImportOptions
    ): AppThunk<Promise<T>> =>
    async (dispatch, getState) => {
        try {
            // Optional. Sockets listeners will catch the ImportStarted event and start the import process, however it will require some time and such manual start improves UX.
            // @see src/components/DashboardNav/components/ImportIndicators.tsx
            dispatch(startOperation({ operation: "import", module, entities: options?.entities }));

            return await query();
        } catch (e: any) {
            if (!options?.shouldSuppressErrorToast?.(e)) {
                toastError(e);
            }

            console.log(`Error while performing an import for module [${module}]}:`, e);
            dispatch(resetOperation({ operation: "import" }));
            return Promise.reject(e);
        }
    };

// ================================================================================
// INTEGRATION EXPORT
// ================================================================================

type ModulesWithIntegrationExportSupport = "susoft";

/**
 * Thunk to perform an integration-based export for a specific module.
 * Unlike `performExport` which handles file-based exports (product lists, stock, etc.),
 * this handles exports to third-party integrations.
 * @param module
 * @param query
 */
export function performIntegrationExport<T>(
    module: ModulesWithIntegrationExportSupport,
    query: () => Promise<T>
): AppThunk {
    return async (dispatch) => {
        try {
            dispatch(startOperation({ operation: "export", module }));

            return await query();
        } catch (e: any) {
            toastError(e);

            console.log(`Error while performing an integration export for module [${module}]:`, e);
            dispatch(resetOperation({ operation: "export" }));
            return Promise.reject(e);
        }
    };
}

// ================================================================================
// PRICE RECONVERSION
// ================================================================================

type ModulesWithPriceReconversionSupport = "product";

/**
 * Thunk to perform an export for a specific module
 * @param module
 * @param query
 */
export function performPriceReconversion<T>(
    module: ModulesWithPriceReconversionSupport,
    query: () => Promise<T>
): AppThunk {
    return async (dispatch, getState) => {
        try {
            dispatch(startOperation({ operation: "price_reconversion", module }));

            return await query();
        } catch (e: any) {
            toastError(e);

            console.log(`Error while performing a price reconversion for module [${module}]}:`, e);
            dispatch(resetOperation({ operation: "price_reconversion" }));
            return Promise.reject(e);
        }
    };
}
